import { z } from "zod";
/**
 * Common order enums and schemas - shared between domain and DTO
 * Contains enums, labels, and common field definitions
 */
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["NEW"] = "NEW";
    OrderStatus["CONFIRMED"] = "CONFIRMED";
    OrderStatus["AWAITING_PAYMENT"] = "AWAITING_PAYMENT";
    OrderStatus["ASSIGNED"] = "ASSIGNED";
    OrderStatus["EN_ROUTE"] = "EN_ROUTE";
    OrderStatus["ARRIVED"] = "ARRIVED";
    OrderStatus["IN_PROGRESS"] = "IN_PROGRESS";
    OrderStatus["CHARGING"] = "CHARGING";
    OrderStatus["COMPLETED"] = "COMPLETED";
    OrderStatus["CANCELLED"] = "CANCELLED";
    OrderStatus["DISPATCHER_CANCELLED"] = "DISPATCHER_CANCELLED";
    OrderStatus["FAILED"] = "FAILED";
    OrderStatus["FAILED_PAYMENT"] = "FAILED_PAYMENT";
})(OrderStatus || (OrderStatus = {}));
export var ChargeLevel;
(function (ChargeLevel) {
    ChargeLevel["TO_PERCENTAGE"] = "TO_PERCENTAGE";
    ChargeLevel["TO_MAXIMUM"] = "TO_MAXIMUM";
    ChargeLevel["BY_TIME_MINUTES"] = "BY_TIME_MINUTES";
})(ChargeLevel || (ChargeLevel = {}));
// Zod схемы для enum'ов
export const zOrderStatusSchema = z.enum([
    OrderStatus.AWAITING_PAYMENT,
    OrderStatus.NEW,
    OrderStatus.CONFIRMED,
    OrderStatus.ASSIGNED,
    OrderStatus.EN_ROUTE,
    OrderStatus.ARRIVED,
    OrderStatus.IN_PROGRESS,
    OrderStatus.CHARGING,
    OrderStatus.COMPLETED,
    OrderStatus.CANCELLED,
    OrderStatus.DISPATCHER_CANCELLED,
    OrderStatus.FAILED,
    OrderStatus.FAILED_PAYMENT,
]);
export const zChargeLevelSchema = z.enum([
    ChargeLevel.TO_PERCENTAGE,
    ChargeLevel.TO_MAXIMUM,
    ChargeLevel.BY_TIME_MINUTES,
]);
// DateRange схема для фильтров
export const zDateRangeSchema = z.object({
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
});
// OrderFilters схема (updated for UC005)
export const zOrderFiltersSchema = z.object({
    status: z.array(zOrderStatusSchema).optional(),
    // New separate date filters as per UC005 specification
    createdDate: zDateRangeSchema.optional(),
    scheduledDate: zDateRangeSchema.optional(),
    // Search functionality as per RQ106
    search: z.string().optional(),
    // Payment status filter
    isPaid: z.boolean().optional(),
    // FR-007 (UC-101): «PRIVATE» / «COMPANY» — по payerType, иначе id конкретной компании
    payer: z.string().optional(),
    // WP-DSP-FIX-03: только заказы без маршрута (нет маршрута или последний маршрут CANCELLED)
    withoutRoute: z.boolean().optional(),
});
export const CHARGE_LEVEL_LABELS = {
    [ChargeLevel.TO_PERCENTAGE]: "До процента",
    [ChargeLevel.TO_MAXIMUM]: "До максимума",
    [ChargeLevel.BY_TIME_MINUTES]: "По времени (мин)",
};
export const getChargeLevelLabel = (chargeLevel) => {
    return CHARGE_LEVEL_LABELS[chargeLevel];
};
export const ORDER_STATUS_LABELS = {
    [OrderStatus.NEW]: "Новый",
    [OrderStatus.CONFIRMED]: "Подтверждён",
    [OrderStatus.AWAITING_PAYMENT]: "Ожидает оплаты",
    [OrderStatus.ASSIGNED]: "Назначен",
    [OrderStatus.EN_ROUTE]: "Техник едет",
    [OrderStatus.ARRIVED]: "Техник прибыл",
    [OrderStatus.IN_PROGRESS]: "Выполняется",
    [OrderStatus.CHARGING]: "Зарядка",
    [OrderStatus.COMPLETED]: "Завершён",
    [OrderStatus.CANCELLED]: "Отменён",
    [OrderStatus.DISPATCHER_CANCELLED]: "Отменён диспетчером",
    [OrderStatus.FAILED]: "Не выполнен",
    [OrderStatus.FAILED_PAYMENT]: "Ошибка оплаты",
};
export const ORDER_STATUS_COLORS = {
    [OrderStatus.NEW]: "default",
    [OrderStatus.CONFIRMED]: "blue",
    [OrderStatus.AWAITING_PAYMENT]: "orange",
    [OrderStatus.ASSIGNED]: "geekblue",
    [OrderStatus.EN_ROUTE]: "processing",
    [OrderStatus.ARRIVED]: "cyan",
    [OrderStatus.IN_PROGRESS]: "processing",
    [OrderStatus.CHARGING]: "green",
    [OrderStatus.COMPLETED]: "success",
    [OrderStatus.CANCELLED]: "error",
    [OrderStatus.DISPATCHER_CANCELLED]: "error",
    [OrderStatus.FAILED]: "red",
    [OrderStatus.FAILED_PAYMENT]: "red",
};
export const getOrderStatusLabel = (status) => {
    return ORDER_STATUS_LABELS[status];
};
/**
 * FR-007: тип плательщика заказа (контракт «Корпоративные клиенты» v2.0 §8).
 * Вычисляется БД и отдаётся во view заказов; фронт его только читает —
 * сменить плательщика созданного заказа нельзя (CP008, решение A10).
 */
export const zPayerTypeSchema = z.enum(["PRIVATE", "COMPANY"]);
export const PAYER_TYPE_LABELS = {
    PRIVATE: "Физлицо",
    COMPANY: "Компания",
};
/**
 * Common order schema - shared fields between domain and DTO
 * Contains the core order fields that are common to both representations
 */
export const zOrderCommonSchema = z.object({
    id: z.string(),
    idClient: z.string(),
    idTariff: z.string(),
    orderNumber: z.string(),
    scheduledDate: z.string(),
    idSubscription: z.string().nullable(),
    chargeLevel: zChargeLevelSchema,
    chargeValue: z.number().nullable(),
    currentCharge: z.number().nullable(),
    status: zOrderStatusSchema,
});
